/**
 * Small, pure normalisers shared by the import job, search and the pages.
 * Safe to import anywhere.
 */

import type { Listing } from "@/lib/construction/types";

/**
 * Whether a company page should be indexed: it has more than the bare register
 * entry (a website, a phone number, verified size data or an OpenStreetMap
 * source). Must match `INDEXABLE` in data.ts, which filters the sitemap.
 */
export function isIndexableListing(l: Pick<Listing, "website" | "phone" | "ranking" | "source">): boolean {
  return Boolean(l.website || l.phone || l.ranking || l.source === "osm");
}

const COMPANY_SUFFIXES = /\b(ltd|limited|plc|p\.l\.c|llp|lp|group|holdings?|uk|\(uk\)|co|company|& co|and co)\b\.?/g;

/** Comparable company name: "Balfour Beatty plc" and "BALFOUR BEATTY" both give "balfour beatty". */
export function nameKey(name: string | null | undefined): string {
  if (!name) return "";
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’"]/g, "")
    .replace(COMPANY_SUFFIXES, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const FULL_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/;
const OUTWARD_ONLY = /^[A-Z]{1,2}\d[A-Z\d]?$/;

/** "m11aa" -> { full: "M1 1AA", outward: "M1" }. Returns nulls for anything that is not a UK postcode. */
export function parsePostcode(value: string | null | undefined): { full: string | null; outward: string | null } {
  if (!value) return { full: null, outward: null };
  const clean = value.toUpperCase().replace(/[^A-Z0-9 ]/g, "").replace(/\s+/g, " ").trim();
  const full = FULL_POSTCODE.exec(clean.replace(/\s/g, "").replace(/^(.+)(\d[A-Z]{2})$/, "$1 $2"));
  if (full) return { full: `${full[1]} ${full[2]}`, outward: full[1] };
  if (OUTWARD_ONLY.test(clean)) return { full: null, outward: clean };
  return { full: null, outward: null };
}

export function isOutwardCode(value: string): boolean {
  return OUTWARD_ONLY.test(value.toUpperCase());
}

/** Digits only, with a leading UK +44 turned into 0, for phone matching. */
export function phoneDigits(value: string | null | undefined): string | null {
  if (!value) return null;
  let digits = value.split(/[;,]/)[0].replace(/\D/g, "");
  if (digits.startsWith("44")) digits = `0${digits.slice(2)}`;
  return digits.length >= 6 ? digits : null;
}

/** First phone number of a possibly multi-valued OSM tag, trimmed. */
export function cleanPhone(value: string | null | undefined): string | null {
  if (!value) return null;
  const first = value.split(";")[0].trim();
  return first.length >= 6 ? first : null;
}

/** Absolute http(s) URL, or null. Adds https:// when the tag has a bare domain. */
export function cleanWebsite(value: string | null | undefined): string | null {
  if (!value) return null;
  let url = value.split(";")[0].trim();
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes(".")) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/** "https://www.example.co.uk/about" -> "example.co.uk". */
export function websiteHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

const KEEP_UPPER = new Set(["uk", "gb", "ni", "plc", "llp", "lp", "cic", "ii", "iii", "iv", "hvac", "mep", "upvc", "pvc", "cctv", "ev", "led", "diy", "jcb", "hgv", "rsj"]);
const KEEP_LOWER = new Set(["and", "of", "the", "for", "in", "on", "at", "to", "a", "an", "by"]);
const ABBREVIATIONS: Record<string, string> = { ltd: "Ltd", st: "St", dr: "Dr", mr: "Mr", mrs: "Mrs", ms: "Ms", rd: "Rd" };

function caseWord(word: string, index: number, initialisms: boolean): string {
  const lower = word.toLowerCase();
  const letters = lower.replace(/[^a-z]/g, "");
  if (!letters) return word;
  if (/^\d+(st|nd|rd|th)[.,]?$/i.test(word)) return lower; // "4TH" -> "4th"
  if (/\d/.test(word)) return word.toUpperCase();
  if (KEEP_UPPER.has(letters)) return word.toUpperCase();
  const abbreviation = ABBREVIATIONS[letters];
  if (abbreviation && /^[a-z]+[.,]?$/.test(lower)) return lower.replace(letters, abbreviation);
  if (index > 0 && KEEP_LOWER.has(lower)) return lower;
  if (initialisms && letters.length <= 4 && !/[aeiouy]/.test(letters)) return word.toUpperCase();
  return lower
    .replace(/(^|[-./(&+,])([a-z])/g, (_, before: string, letter: string) => before + letter.toUpperCase())
    .replace(/(^|[-(])([A-Za-z])['’]([a-z])/g, (_, before: string, first: string, next: string) => `${before}${first.toUpperCase()}'${next.toUpperCase()}`)
    // Place names keep their small joining words: "Stratford-upon-Avon", "Southend-on-Sea".
    .replace(/-(Upon|On|In|Under|By|Le|La|The|En|Sur|De)(?=-)/g, (_, small: string) => `-${small.toLowerCase()}`);
}

function caseWords(value: string, initialisms: boolean): string {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, i) => caseWord(word, i, initialisms))
    .join(" ");
}

/**
 * Companies House names are stored in capitals ("SMITH & SONS BUILDERS LTD").
 * This gives the readable form shown on the page ("Smith & Sons Builders Ltd");
 * initialisms without vowels ("JBS", "M&E") stay in capitals. The register's
 * own spelling is kept separately (ch_name).
 */
export function displayCompanyName(raw: string): string {
  // Leading punctuation (".A. PARK ROOFING LTD") only exists to jump A to Z lists.
  return caseWords(raw.replace(/^[^A-Za-z0-9]+/, ""), true) || caseWords(raw, true);
}

/** "UNIT 4, HIGHFIELD COURT" -> "Unit 4, Highfield Court". */
export function displayAddressPart(raw: string): string {
  return caseWords(raw, false);
}

/** Stable 32-bit hash of a string (FNV-1a). Used to vary page wording by slug. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function formatCount(n: number): string {
  return n.toLocaleString("en-GB");
}

/** "1 builder" / "3 builders". */
export function plural(n: number, singular: string, pluralWord: string): string {
  return `${formatCount(n)} ${n === 1 ? singular : pluralWord}`;
}

/** Joins ["a", "b", "c"] as "a, b and c". */
export function listJoin(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** "1 construction company" / "1,234 construction companies". */
export function countCompanies(n: number): string {
  return `${formatCount(n)} construction ${n === 1 ? "company" : "companies"}`;
}
